import { Fragment, useState } from 'react'
import {
    Box,
    Collapse,
    FormControlLabel,
    Paper,
    Skeleton,
    styled,
    Typography,
} from '@mui/material'
import {
    AttributeValue,
    Checkbox,
    StatusIndicator,
    WarningModal,
} from 'packages/eid-ui'
import { useCsvInbox, useCloseInboxProcess } from 'hooks'
import { useTranslation } from 'react-i18next'
import useSubcomponents from 'useSubcomponents'
import { MOBILE_SCREEN_WIDTH } from 'packages/core/utils/constants'
import { Icon } from 'packages/eid-icons'

import ItemDetails from 'components/ItemDetails'
import { AttributeSections } from 'components/AttributeSections'
import { getOverviewAttributes } from './overviewAttributes'
import LandscapeTimeline from './LandscapeTimeline'
import GroupToBusinessRoleGrid from '../GroupToBusinessRoleGrid'
import { GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE, RTA_INBOX_TYPES } from 'utils'
import GroupEligibilityGrid from '../GroupEligibility'
import GenericButton from 'components/Button/GenericButton'
import ApplicationRoleAttributeGrid from '../ApplicationRoleAttribute'
import TemplateBusinessRoleGrid from '../TemplateBusinessRole'
import DerivedBusinessRoleGrid from '../DerivedBusinessRole'

const getItemStatus = (status) => {
    switch (status) {
        case 'Processed':
            return 'Completed'
        case 'Not Processed':
            return 'NotStarted'
        case 'Processing':
            return 'Continued'
        case 'Error':
            return 'Error'
        case 'Canceled':
            return 'Canceled'
        case 'Closed':
            return 'AutoClosed'
        case 'Open':
            return 'Open'
        default:
            return 'Default'
    }
}

const smallScreenWidth = 960

// Status configurations for tabs (from InboxStatusIndicator)
const tabStatusConfig = {
    invalid: {
        icon: 'Close',
        iconColor: '#fff',
        backgroundColor: '#d0021b',
        statusId: GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE.Invalid,
    },
    valid: {
        icon: 'Check',
        iconColor: '#fff',
        backgroundColor: '#1eb77b',
        color: '#1eb77b',
        statusId: GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE.Valid,
    },
    ignored: {
        icon: 'Defer',
        iconColor: '#ffffff',
        backgroundColor: '#5d6870',
        color: '#ffffff',
        statusId: GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE.Ignored,
    },
    completed: {
        icon: 'Stop',
        iconColor: '#ffffff',
        backgroundColor: '#0057a0',
        statusId: GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE.Completed,
    },
    notValidated: {
        icon: 'NotStartedStatus',
        iconColor: '#fffff',
        backgroundColor: '#ffb800',
        color: '#5b6871',
        statusId: GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE.NotValidated,
    },
    error: {
        icon: 'Exclamation',
        iconColor: '#fff',
        backgroundColor: '#d14e4e',
        color: '#ffffff',
        statusId: GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE.Error,
    },
}

const subTabs = [
    {
        value: 'all',
        labelKey: 'All',
        display: true,
        showCount: true,
    },
    {
        value: 'invalid',
        labelKey: 'Invalid',
        display: true,
        statusConfig: tabStatusConfig.invalid,
    },
    {
        value: 'valid',
        labelKey: 'Valid',
        display: true,
        statusConfig: tabStatusConfig.valid,
    },
    {
        value: 'ignored',
        labelKey: 'Ignored',
        display: true,
        statusConfig: tabStatusConfig.ignored,
    },
    {
        value: 'completed',
        labelKey: 'Completed',
        display: true,
        statusConfig: tabStatusConfig.completed,
    },
    {
        value: 'notValidated',
        labelKey: 'NotValidated',
        display: true,
        statusConfig: tabStatusConfig.notValidated,
    },
    {
        value: 'error',
        labelKey: 'Error',
        display: true,
        statusConfig: tabStatusConfig.error,
    },
]

const canEditInboxControl = 'RTA-CSVInbox-CanEditGrid-Control'
const canCloseInboxProcessControl = 'RTA-CSVInbox-CanCloseInboxProcess-Control'

export const Overview = (props) => {
    const { csvInboxId } = props
    const { data, isLoading, refetch, isFetching } = useCsvInbox(csvInboxId)

    const { t } = useTranslation()

    const { trimControlsBasedOnAccess, hasAccessToControl } = useSubcomponents()
    const canEditInbox = hasAccessToControl(canEditInboxControl)
    const subTabsAccess = trimControlsBasedOnAccess(
        subTabs.filter((t) => t.display),
    )
    const [subActiveTab, setSubActiveTab] = useState(
        subTabsAccess?.length > 0 ? subTabsAccess[0].value : null,
    )
    const [isExpanded, setIsExpanded] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [refreshTrigger, setRefreshTrigger] = useState(false)
    const overviewAttributes = getOverviewAttributes()
    const [isCloseInboxProcessModalOpen, setIsCloseInboxProcessModalOpen] =
        useState(false)
    const [closeInboxProcessCheck, setCloseInboxProcessCheck] = useState(false)
    const {
        mutateAsync: closeInboxProcess,
        isLoading: isCloseInboxProcessLoading,
    } = useCloseInboxProcess()

    const handleCloseInboxProcess = async () => {
        await closeInboxProcess({
            trackingID: data?.trackingID,
        })
        setIsCloseInboxProcessModalOpen(false)
        setCloseInboxProcessCheck(false)
        refetch()
        setRefreshTrigger((prev) => !prev)
    }

    const handleRefresh = () => {
        setRefreshTrigger((prev) => !prev)
        // refetch()
    }

    const applicationDetails = (
        <Fragment>
            <Box
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <AttributeSections
                    attributes={overviewAttributes}
                    data={data}
                    hideNullValues={true}
                />
            </Box>
        </Fragment>
    )

    const renderContent = () => (
        <Fragment>
            <ApplicationContainer>
                {true && (
                    <ApplicationWrapper>
                        {applicationDetails}
                    </ApplicationWrapper>
                )}
            </ApplicationContainer>
        </Fragment>
    )

    return (
        <>
            {isLoading ? (
                <Box width={'100%'}>
                    <Skeleton height={40} />
                </Box>
            ) : (
                <Box>
                    {/* Overview Paper Section */}
                    <OverviewPaperWrapper>
                        <PaperContainer isExpanded={isExpanded}>
                            {data && (
                                <>
                                    <StatusBadgeWrapper>
                                        <StatusIndicator
                                            variant={getItemStatus(
                                                data.rtA_CSVInboxStatusName,
                                            )}
                                            label={t(
                                                data.rtA_CSVInboxStatusName ||
                                                    'Unknown',
                                            )}
                                            size="medium"
                                        />
                                    </StatusBadgeWrapper>
                                </>
                            )}
                            <Box display="flex">
                                <Box width="85%">{renderContent()}</Box>
                                <Box width="15%">
                                    {data?.isClosed ||
                                    !canEditInbox ||
                                    !hasAccessToControl(
                                        canCloseInboxProcessControl,
                                    ) ? null : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <GenericButton
                                                rootStylesProp={{
                                                    backgroundColor: '#ffffff',
                                                    color: '#0057a0',
                                                    border: 'solid 1px #0057a0',
                                                    borderRadius: '4px',
                                                    padding: '8px 16px',
                                                    fontSize: '12px',
                                                    textTransform: 'uppercase',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            '#f5f5f5',
                                                    },
                                                }}
                                                onClick={() => {
                                                    setIsCloseInboxProcessModalOpen(
                                                        true,
                                                    )
                                                }}
                                            >
                                                {t('CloseInboxProcess')}
                                            </GenericButton>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </PaperContainer>

                        {/* Show More/Less Button - positioned at bottom border of first paper */}
                        <ShowMoreButtonWrapper>
                            <ShowMoreButton
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <ShowMoreText>
                                    {isExpanded
                                        ? t('HideLandscapes')
                                        : t('ShowLandscapes')}
                                </ShowMoreText>
                                <ChevronWrapper isExpanded={isExpanded}>
                                    <Icon
                                        name="Arrow"
                                        direction="down"
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                        }}
                                    />
                                </ChevronWrapper>
                            </ShowMoreButton>
                        </ShowMoreButtonWrapper>
                    </OverviewPaperWrapper>

                    {/* Collapsible Paper - separate paper with 0 gap */}
                    <CollapsibleWrapper>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <CollapsiblePaper
                                elevation={1}
                                sx={{
                                    paddingRight: '10%',
                                }}
                            >
                                <LandscapeTimeline
                                    refetch={refetch}
                                    isInboxClosed={
                                        data?.rtA_CSVInboxStatusName ===
                                        'Closed'
                                    }
                                    trackingID={data?.trackingID}
                                    inboxTypeID={data?.csvInboxTypeID}
                                    canEditInbox={canEditInbox}
                                />
                            </CollapsiblePaper>
                        </Collapse>
                    </CollapsibleWrapper>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',

                            // gap: '8px',
                        }}
                    >
                        {data && subTabsAccess.length > 0 && (
                            <TabsSection>
                                <ItemDetails.Tabs
                                    value={subActiveTab}
                                    variant={'scrollable'}
                                    style={{ border: 'none' }}
                                    onChange={(_, value) => {
                                        setSubActiveTab(value)
                                    }}
                                >
                                    {subTabsAccess.map((tab, index) => {
                                        return (
                                            <ItemDetails.Tabs.Tab
                                                data-protectedsubcomponent={
                                                    subTabsAccess.find(
                                                        (x) =>
                                                            x.value ===
                                                            tab.value,
                                                    )?.requireAccess
                                                }
                                                key={tab.value + index}
                                                value={tab.value}
                                                label={
                                                    <TabLabelWrapper>
                                                        <span>
                                                            {t(tab.labelKey)}
                                                        </span>
                                                        {tab.statusConfig && (
                                                            <StatusIconBadge
                                                                style={{
                                                                    backgroundColor:
                                                                        tab
                                                                            .statusConfig
                                                                            .backgroundColor,
                                                                }}
                                                            >
                                                                <Icon
                                                                    name={
                                                                        tab
                                                                            .statusConfig
                                                                            .icon
                                                                    }
                                                                    color={
                                                                        tab
                                                                            .statusConfig
                                                                            .iconColor
                                                                    }
                                                                    style={{
                                                                        width: '10px',
                                                                        height: '10px',
                                                                        fill: tab
                                                                            .statusConfig
                                                                            .iconColor,
                                                                    }}
                                                                />
                                                            </StatusIconBadge>
                                                        )}
                                                        {tab.showCount && (
                                                            <span
                                                                style={{
                                                                    fontSize:
                                                                        '12px',
                                                                    color: '#5d6870',
                                                                }}
                                                            >
                                                                {totalCount > 0
                                                                    ? totalCount.toString()
                                                                    : ''}{' '}
                                                            </span>
                                                        )}
                                                    </TabLabelWrapper>
                                                }
                                            />
                                        )
                                    })}
                                </ItemDetails.Tabs>
                            </TabsSection>
                        )}
                        <GenericButton
                            onClick={handleRefresh}
                            rootStylesProp={{
                                marginBottom: '8px',
                            }}
                        >
                            <Icon
                                name="Refresh"
                                color="#0057a0"
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    fill: '#0057a0',
                                }}
                            />
                        </GenericButton>
                    </Box>

                    {/* Tab Content */}
                    {data?.csvInboxTypeID ===
                        RTA_INBOX_TYPES.GROUP_TO_BUSINESS_ROLE && (
                        <GroupToBusinessRoleGrid
                            trackingID={data?.trackingID}
                            statusId={
                                subTabs.find(
                                    (tab) => tab.value === subActiveTab,
                                )?.statusConfig?.statusId
                            }
                            setTotalCount={setTotalCount}
                            subActiveTab={subActiveTab}
                            refreshTrigger={refreshTrigger}
                            canEditInbox={canEditInbox}
                        />
                    )}
                    {data?.csvInboxTypeID ===
                        RTA_INBOX_TYPES.GROUP_ELIGIBILITY && (
                        <Box sx={{ minWidth: 0 }}>
                            <GroupEligibilityGrid
                                trackingID={data?.trackingID}
                                statusId={
                                    subTabs.find(
                                        (tab) => tab.value === subActiveTab,
                                    )?.statusConfig?.statusId
                                }
                                setTotalCount={setTotalCount}
                                subActiveTab={subActiveTab}
                                refreshTrigger={refreshTrigger}
                                canEditInbox={canEditInbox}
                            />
                        </Box>
                    )}
                    {data?.csvInboxTypeID ===
                        RTA_INBOX_TYPES.APPLICATION_ROLE_ATTRIBUTE && (
                        <ApplicationRoleAttributeGrid
                            trackingID={data?.trackingID}
                            statusId={
                                subTabs.find(
                                    (tab) => tab.value === subActiveTab,
                                )?.statusConfig?.statusId
                            }
                            setTotalCount={setTotalCount}
                            subActiveTab={subActiveTab}
                            refreshTrigger={refreshTrigger}
                            canEditInbox={canEditInbox}
                        />
                    )}
                    {data?.csvInboxTypeID ===
                        RTA_INBOX_TYPES.TEMPLATE_BUSINESS_ROLE && (
                        <TemplateBusinessRoleGrid
                            trackingID={data?.trackingID}
                            statusId={
                                subTabs.find(
                                    (tab) => tab.value === subActiveTab,
                                )?.statusConfig?.statusId
                            }                       
                            setTotalCount={setTotalCount}
                            subActiveTab={subActiveTab}
                            refreshTrigger={refreshTrigger}
                            canEditInbox={canEditInbox}
                        />
                    )}
                    {data?.csvInboxTypeID ===
                        RTA_INBOX_TYPES.DERIVED_BUSINESS_ROLE && (
                        <DerivedBusinessRoleGrid
                            trackingID={data?.trackingID}
                            statusId={
                                subTabs.find(
                                    (tab) => tab.value === subActiveTab,
                                )?.statusConfig?.statusId
                            }
                            setTotalCount={setTotalCount}
                            subActiveTab={subActiveTab}
                            refreshTrigger={refreshTrigger}
                            canEditInbox={canEditInbox}
                        />
                    )}
                </Box>
            )}
            {isCloseInboxProcessModalOpen && (
                <WarningModal
                    title={t('CloseInbox') + ` ${data?.fileName}`}
                    description={t('CloseInboxProcessWarningMessage')}
                    yesLabel={t('Proceed')}
                    noLabel={t('Cancel')}
                    open={isCloseInboxProcessModalOpen}
                    cancel={() => {
                        setIsCloseInboxProcessModalOpen(false)
                    }}
                    proceed={handleCloseInboxProcess}
                    proceeding={isCloseInboxProcessLoading}
                    disableProceedButton={
                        isCloseInboxProcessLoading || !closeInboxProcessCheck
                    }
                >
                    <Box sx={{ padding: '1.6rem 1.6rem' }}>
                        <FormControlLabel
                            onChange={() =>
                                setCloseInboxProcessCheck((p) => !p)
                            }
                            control={
                                <Checkbox checked={closeInboxProcessCheck} />
                            }
                            label={t('CloseInboxProcess')}
                        />
                    </Box>
                </WarningModal>
            )}
        </>
    )
}

const StyledAttributeValue = styled(AttributeValue)(({ theme }) => ({
    color: theme?.palette?.primary?.main,
    textTransform: 'none',
}))

const OverviewPaperWrapper = styled(Box)(() => ({
    position: 'relative',
    marginBottom: '16px', // Space for the button that hangs below
}))

export const PaperContainer = styled(Paper)(
    ({ isSmallScreen, isExpanded }) => ({
        position: 'relative',
        padding: isSmallScreen ? '2.3rem' : '3.2rem 1.6rem',
        paddingBottom: '24px',
        borderRadius: '8px',
        // Remove bottom border radius when expanded so it connects with collapsible paper
        borderBottomLeftRadius: isExpanded ? 0 : '8px',
        borderBottomRightRadius: isExpanded ? 0 : '8px',
        [`@media (max-width:${smallScreenWidth - 1}px)`]: {
            overflow: 'hidden',
        },
    }),
)

const StatusBadgeWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '-16px',
    right: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#fff',
    // padding: '8px 12px',
    // borderRadius: '8px',
    // border: '1px solid #e0e0e0',
    // boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    zIndex: 1,
}))

const StatusLabel = styled(Typography)(() => ({
    fontSize: '11px',
    fontWeight: 600,
    color: '#8b909a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
}))

export const ItemWrapper = styled(Box)(() => ({
    marginLeft: '-1.8rem',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 15,
}))
export const ApplicationWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    flexWrap: 'wrap',
    width: '100%',
    [`@media (max-width:${MOBILE_SCREEN_WIDTH - 1}px)`]: {
        display: 'none',
    },
}))
export const ApplicationContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    // padding: theme.spacing(2),
    [`@media (max-width:${MOBILE_SCREEN_WIDTH - 1}px)`]: {
        paddingBottom: theme.spacing(2),
    },
    [`@media (max-width:345px)`]: {
        flexDirection: 'column-reverse',
    },
}))

const ShowMoreButtonWrapper = styled(Box)(() => ({
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translate(-50%, 50%)',
    zIndex: 2,
}))

const ShowMoreButton = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 20px',
    borderRadius: '20px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
    },
}))

const ShowMoreText = styled(Typography)(() => ({
    fontSize: '12px',
    color: '#0057a0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
}))

const ChevronWrapper = styled(Box)(({ isExpanded }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.3s ease',
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    // '& svg': {
    //     width: '12px',
    //     height: '12px',
    // },
}))

const TabsSection = styled(Box)(() => ({
    marginTop: '32px',
}))

const TabLabelWrapper = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
}))

const StatusIconBadge = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '16px',
    borderRadius: '4px',
}))

const CollapsibleWrapper = styled(Box)(() => ({
    marginTop: '-16px', // Negative margin to attach with upper paper
}))

const CollapsiblePaper = styled(Paper)(() => ({
    padding: '24px 1.6rem',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    boxShadow:
        '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
}))
